import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users as UsersIcon, Search, Plus,
  MoreVertical, Edit, Trash2, Mail, Phone, Calendar,
  CheckCircle, XCircle, ChevronLeft, ChevronRight,
  Shield, UserCheck, AlertCircle, LogIn,
  ToggleLeft, ToggleRight, CreditCard, X, Sparkles
} from 'lucide-react';
import { EyeIcon, EyeOffIcon } from '../components/EyeIcons';
import toast from 'react-hot-toast';
import userService from '../services/userService';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';
// Import EnhancedDataTable V2 directly to ensure updated pagination - v2
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import EnhancedModal from '../components/EnhancedModal';
import { ResponsiveForm, ResponsiveInput, ResponsiveSelect, ResponsiveButton, ResponsiveCheckbox } from '../components/ResponsiveModal';
import { CreateButton, SaveButton, DeleteButton, CancelButton, UpdateButton } from '../components/CommonButtons';
import Switch from '../components/Switch';
import { validateEmail, validatePassword, validatePhone, validateName, validateRequired, ErrorMessage } from '../utils/validation';
import { getRoleBasedDashboard } from '../utils/roleRedirect';

const Users = () => {
  const { user: currentUser, loginAs } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [roles, setRoles] = useState([]);
  const [showActionsMenu, setShowActionsMenu] = useState({});
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedUserForPlan, setSelectedUserForPlan] = useState(null);
  const [newPlanId, setNewPlanId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    password: '',
    confirm_password: '',
    role: 'user',  // Default to 'user' role
    is_active: 1  // Number: 1 for Active (checked), 0 for Inactive (unchecked)
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Helper function to check if user can be deleted
  const canDeleteUser = (user) => {
    // Check if user object exists
    if (!user || !user.id) {
      return false;
    }
    
    // Prevent deleting the currently logged-in user
    if (currentUser && currentUser.id && user.id === currentUser.id) {
      return false;
    }
    
    // Prevent deleting super-admin users
    const userRoles = user.roles || [];
    const hasSuperAdminRole = userRoles.some(role => {
      // Handle both string and object role formats
      const roleName = typeof role === 'string' ? role : (role?.name || role?.role || '');
      const normalizedRole = roleName.toLowerCase().trim();
      return normalizedRole === 'super-admin' || normalizedRole === 'superadmin';
    });
    
    return !hasSuperAdminRole;
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchTerm, selectedRole, selectedStatus, perPage]);

  useEffect(() => {
    fetchRoles();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowActionsMenu({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        role: selectedRole,
        is_active: selectedStatus  // Changed from 'status' to 'is_active'
      };

      const response = await userService.getUsers(params);
      
      
      // Use normalized response structure
      const userData = response.data || [];
      
      // Validate each user object
      const validatedUsers = userData.map(user => {
        if (!user || typeof user !== 'object') {
          return null;
        }
        return user;
      }).filter(Boolean);
      
      setUsers(validatedUsers);
      
      // Handle pagination from normalized response
      if (response.pagination) {
        setTotalUsers(response.pagination.total || 0);
        setTotalPages(response.pagination.last_page || 1);
        setCurrentPage(response.pagination.current_page || 1);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
      setUsers([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // This would typically fetch from an API endpoint
      // For now, using static roles based on actual API response
      setRoles([
        { id: 1, name: 'super-admin', display_name: 'Super Admin' },
        { id: 2, name: 'user', display_name: 'User' }
      ]);
    } catch (error) {
    }
  };



  const handleDeleteUser = async () => {
    try {
      await userService.deleteUser(userToDelete.id);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };


  const handleStatusUpdate = async (userId, newStatus) => {
    try {
      await userService.updateUserStatus(userId, newStatus);
      toast.success('User status updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await userService.toggleUserStatus(userId);
      toast.success('User status toggled successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to toggle user status');
    }
  };

  const handleLoginAsUser = async (userId) => {
    try {
      const result = await loginAs(userId);
      if (result?.success) {
        // Get the impersonated user data from localStorage
        const impersonatedUserData = localStorage.getItem('user');
        if (impersonatedUserData) {
          const impersonatedUser = JSON.parse(impersonatedUserData);
          // Use role-based redirect for the impersonated user
          const redirectUrl = getRoleBasedDashboard(impersonatedUser);
          window.location.href = redirectUrl;
        } else {
          // Fallback to dashboard if user data not found
          window.location.href = '/dashboard';
        }
      }
    } catch (error) {
      // Error toast is already handled in loginAs function
    }
  };

  const handleChangePlan = async () => {
    if (!selectedUserForPlan || !newPlanId) return;
    
    try {
      await userService.changeUserPlan(selectedUserForPlan.id, newPlanId);
      toast.success('User plan changed successfully');
      setShowChangePlanModal(false);
      setSelectedUserForPlan(null);
      setNewPlanId('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to change user plan');
    }
  };


  const validateUserForm = (isEdit = false) => {
    const errors = {};
    
    // First Name validation
    const firstNameError = validateName(formData.first_name, 'First name');
    if (firstNameError) errors.first_name = firstNameError;
    
    // Last Name validation
    const lastNameError = validateName(formData.last_name, 'Last name');
    if (lastNameError) errors.last_name = lastNameError;
    
    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) errors.email = emailError;
    
    // Phone validation
    const phoneError = validatePhone(formData.phone_no);
    if (phoneError) errors.phone_no = phoneError;
    
    // Password validation (only for create)
    if (!isEdit) {
      const passwordError = validatePassword(formData.password);
      if (passwordError) errors.password = passwordError;

      // Confirm password validation
      if (!formData.confirm_password) {
        errors.confirm_password = 'Please confirm your password';
      } else if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    }

    // Role is always 'user' - no validation needed

    // Status validation - ensure it's either 0 or 1
    if (formData.is_active !== 0 && formData.is_active !== 1) {
      errors.is_active = 'Invalid status value';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateUserForm(false)) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    try {
      const response = await userService.createUser(formData);
      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!validateUserForm(true)) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    try {
      const updateData = { ...formData };
      // Don't send password if it's empty
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await userService.updateUser(selectedUserForEdit.id, updateData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUserForEdit(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone_no: '',
      password: '',
      confirm_password: '',
      role: 'user',  // Default to 'user' role
      is_active: 1  // Default to Active (checked)
    });
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const openEditModal = (user) => {
    if (!user) {
      return;
    }
    setSelectedUserForEdit(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone_no: user.phone_no || '',
      password: '', // Don't populate password
      role: 'user',  // Always use 'user' role
      is_active: user.is_active ? 1 : 0  // Convert to number: 1 for checked, 0 for unchecked
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (user) => {
    if (!user) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    // Use is_active_text if available, otherwise determine based on is_active value
    const isActive = user.is_active === 1 || user.is_active === true;
    const statusText = user.is_active_text || (isActive ? 'Active' : 'Inactive');
    
    // Determine badge color and icon based on is_active value
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          {statusText}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          {statusText}
        </span>
      );
    }
    
    // Default fallback
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Unknown
      </span>
    );
  };

  const getRoleBadge = (user) => {
    if (!user) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          No Role
        </span>
      );
    }
    const userRoles = user.roles || [];
    if (userRoles.length === 0) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          No Role
        </span>
      );
    }
    
    const primaryRole = userRoles[0];
    const roleColors = {
      'super-admin': 'purple',
      'user': 'blue'
    };
    
    const color = roleColors[primaryRole.name] || 'gray';
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${color}-100 text-${color}-800`}>
        <Shield className="w-3 h-3 mr-1" />
        {primaryRole.display_name || primaryRole.name}
      </span>
    );
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <p className="text-sm text-gray-600 mt-1">Manage your platform users and their permissions</p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-1 gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Role Filter */}
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Roles</option>
              {Array.isArray(roles) && roles.map(role => (
                <option key={role.id} value={role.name}>
                  {role.display_name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3">
            {/* Add User */}
            <CreateButton
              onClick={() => setShowCreateModal(true)}
              size="sm"
            >
              Add User
            </CreateButton>
          </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <EnhancedDataTable
          columns={[
            {
              key: 'user',
              header: 'User',
              sortable: true,
              render: (value, user) => {
                if (!user || !user.first_name || !user.last_name) {
                  return <div className="text-gray-400">N/A</div>;
                }
                return (
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                        {user.first_name.charAt(0).toUpperCase()}{user.last_name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: #{user.id}
                      </div>
                    </div>
                  </div>
                );
              }
            },
            {
              key: 'contact',
              header: 'Contact',
              sortable: false,
              render: (value, user) => {
                if (!user) {
                  return <div className="text-gray-400">N/A</div>;
                }
                return (
                  <div>
                    <div className="text-sm text-gray-900 flex items-center gap-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {user.email || 'No email'}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {user.phone_no || 'No phone'}
                    </div>
                  </div>
                );
              }
            },
            {
              key: 'role',
              header: 'Role',
              sortable: true,
              render: (value, user) => getRoleBadge(user)
            },
            {
              key: 'status',
              header: 'Status',
              sortable: true,
              render: (value, user) => (
                <Switch
                  checked={user.is_active === 1 || user.is_active === true}
                  onChange={() => handleToggleStatus(user.id)}
                  tooltip={user.is_active ? 'Click to deactivate' : 'Click to activate'}
                />
              )
            },
            {
              key: 'joined',
              header: 'Joined',
              sortable: true,
              render: (value, user) => {
                if (!user || !user.created_at) {
                  return <div className="text-gray-400">N/A</div>;
                }
                return (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {new Date(user.created_at).toLocaleDateString()}
                  </div>
                );
              }
            }
          ]}
          data={users}
          loading={loading}
          showColumnControls={true}
          showRefresh={false}
          enableColumnResize={true}
          enableDragDrop={true}
          emptyMessage={
            searchTerm || selectedRole || selectedStatus
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by creating a new user'
          }
          actions={(user) => {
            if (!user || !user.id) {
              return null;
            }
            return (
              <div className="flex items-center justify-end">
                <div className="relative">
                  <button
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const spaceBelow = window.innerHeight - rect.bottom;
                      const spaceAbove = rect.top;
                      const dropdownHeight = 250; // Approximate height of dropdown with more items

                      // Always open downward if there's enough space below OR if we're near the top
                      // Only open upward if there's not enough space below AND we're not near the top
                      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

                      setShowActionsMenu({
                        [user.id]: !showActionsMenu[user.id],
                        [`${user.id}_position`]: shouldOpenUpward ? 'top' : 'bottom',
                        [`${user.id}_rect`]: rect
                      });
                    }}
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>

                  {showActionsMenu[user.id] && (
                    <div
                      className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto"
                      style={{
                        zIndex: 9999,
                        right: `${window.innerWidth - showActionsMenu[`${user.id}_rect`]?.right}px`,
                        ...(showActionsMenu[`${user.id}_position`] === 'top' ? {
                          bottom: `${window.innerHeight - showActionsMenu[`${user.id}_rect`]?.top + 8}px`
                        } : {
                          top: `${showActionsMenu[`${user.id}_rect`]?.bottom + 8}px`
                        })
                      }}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            openEditModal(user);
                            setShowActionsMenu({});
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit User
                        </button>
                        <button
                          onClick={() => {
                            handleToggleStatus(user.id);
                            setShowActionsMenu({});
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          {user.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          Toggle Status
                        </button>
                        <button
                          onClick={() => {
                            handleLoginAsUser(user.id);
                            setShowActionsMenu({});
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <LogIn className="w-4 h-4" />
                          Login as User
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUserForPlan(user);
                            setShowChangePlanModal(true);
                            setShowActionsMenu({});
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <CreditCard className="w-4 h-4" />
                          Change Plan
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => {
                            if (canDeleteUser(user)) {
                              setUserToDelete(user);
                              setShowDeleteModal(true);
                              setShowActionsMenu({});
                            }
                          }}
                          className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                            canDeleteUser(user) 
                              ? "text-red-600 hover:bg-red-50" 
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                          disabled={!canDeleteUser(user)}
                          title={canDeleteUser(user) 
                            ? "Delete user" 
                            : currentUser && user.id === currentUser.id 
                              ? "Cannot delete your own account"
                              : "Cannot delete super-admin user"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete User
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }}
          pagination={{
            current_page: currentPage,
            per_page: perPage,
            total: totalUsers,
            last_page: totalPages
          }}
          onPageChange={setCurrentPage}
          itemName="users"
          onSort={(key, direction) => {
            // Handle sorting - you can implement server-side or client-side
          }}
          defaultSortKey="joined"
          defaultSortDirection="desc"
        />
            
        {/* Empty State */}
        {users.length === 0 && !loading && (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedRole || selectedStatus
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by creating a new user'}
            </p>
            {(searchTerm || selectedRole || selectedStatus) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRole('');
                  setSelectedStatus('');
                }}
                className="mt-4 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {users.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * perPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * perPage, totalUsers)}</span> of{' '}
                  <span className="font-medium">{totalUsers}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`relative inline-flex items-center px-3 py-1.5 border text-sm font-medium ${
                        currentPage === idx + 1
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        title="Delete User"
        size="small"
        showCloseButton={true}
      >
        <p className="text-sm text-gray-500 mb-4">
          Are you sure you want to delete <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <CancelButton
            onClick={() => {
              setShowDeleteModal(false);
              setUserToDelete(null);
            }}
          />
          <DeleteButton
            onClick={handleDeleteUser}
          >
            Delete
          </DeleteButton>
        </div>
      </EnhancedModal>

      {/* Change Plan Modal */}
      <EnhancedModal
        isOpen={showChangePlanModal}
        onClose={() => {
          setShowChangePlanModal(false);
          setSelectedUserForPlan(null);
          setNewPlanId('');
        }}
        title="Change User Plan"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            <p className="text-sm text-gray-500 mb-4">
              Change subscription plan for <strong>{selectedUserForPlan?.first_name} {selectedUserForPlan?.last_name}</strong>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select New Plan
              </label>
              <select
                value={newPlanId}
                onChange={(e) => setNewPlanId(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">Choose a plan</option>
                <option value="1">Basic Plan</option>
                <option value="2">Professional Plan</option>
                <option value="3">Enterprise Plan</option>
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedUserForPlan(null);
                  setNewPlanId('');
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <UpdateButton
                onClick={handleChangePlan}
                disabled={!newPlanId}
              >
                Change Plan
              </UpdateButton>
            </div>
        </div>
      </EnhancedModal>

      {/* Create User Modal */}
      <EnhancedModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New User"
        size="xlarge"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => {
                    setFormData({ ...formData, first_name: e.target.value });
                    if (formErrors.first_name) {
                      setFormErrors({ ...formErrors, first_name: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.first_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.first_name && <ErrorMessage error={formErrors.first_name} />}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => {
                    setFormData({ ...formData, last_name: e.target.value });
                    if (formErrors.last_name) {
                      setFormErrors({ ...formErrors, last_name: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.last_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.last_name && <ErrorMessage error={formErrors.last_name} />}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value.toLowerCase() });
                    if (formErrors.email) {
                      setFormErrors({ ...formErrors, email: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.email && <ErrorMessage error={formErrors.email} />}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone_no}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, phone_no: value });
                    if (formErrors.phone_no) {
                      setFormErrors({ ...formErrors, phone_no: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.phone_no ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  placeholder="1234567890"
                  required
                />
                {formErrors.phone_no && <ErrorMessage error={formErrors.phone_no} />}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (formErrors.password) {
                        setFormErrors({ ...formErrors, password: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm pr-10 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    placeholder="Min 6 chars, 1 uppercase, 1 lowercase, 1 number"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeIcon className="h-5 w-5 cursor-pointer" />
                    ) : (
                      <EyeOffIcon className="h-5 w-5 cursor-pointer" />
                    )}
                  </button>
                </div>
                {formErrors.password && <ErrorMessage error={formErrors.password} />}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => {
                      setFormData({ ...formData, confirm_password: e.target.value });
                      if (formErrors.confirm_password) {
                        setFormErrors({ ...formErrors, confirm_password: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm pr-10 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.confirm_password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    placeholder="Re-enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeIcon className="h-5 w-5 cursor-pointer" />
                    ) : (
                      <EyeOffIcon className="h-5 w-5 cursor-pointer" />
                    )}
                  </button>
                </div>
                {formErrors.confirm_password && <ErrorMessage error={formErrors.confirm_password} />}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Switch
                checked={formData.is_active === 1}
                onChange={(value) => setFormData({ ...formData, is_active: value ? 1 : 0 })}
                label={formData.is_active === 1 ? 'Active User' : 'Inactive User'}
              />
            </div>

            <div className="flex justify-end gap-3">
              <CancelButton
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              />
              <SaveButton
                onClick={handleCreateUser}
              >
                Create User
              </SaveButton>
            </div>
        </div>
      </EnhancedModal>

      {/* Edit User Modal */}
      <EnhancedModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUserForEdit(null);
          resetForm();
        }}
        title="Edit User"
        size="xlarge"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => {
                    setFormData({ ...formData, first_name: e.target.value });
                    if (formErrors.first_name) {
                      setFormErrors({ ...formErrors, first_name: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.first_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.first_name && <ErrorMessage error={formErrors.first_name} />}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => {
                    setFormData({ ...formData, last_name: e.target.value });
                    if (formErrors.last_name) {
                      setFormErrors({ ...formErrors, last_name: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.last_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.last_name && <ErrorMessage error={formErrors.last_name} />}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value.toLowerCase() });
                    if (formErrors.email) {
                      setFormErrors({ ...formErrors, email: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {formErrors.email && <ErrorMessage error={formErrors.email} />}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone_no}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, phone_no: value });
                    if (formErrors.phone_no) {
                      setFormErrors({ ...formErrors, phone_no: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.phone_no ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  placeholder="1234567890"
                  required
                />
                {formErrors.phone_no && <ErrorMessage error={formErrors.phone_no} />}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password (leave blank to keep current)
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Leave blank to keep current password"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Switch
                checked={formData.is_active === 1}
                onChange={(value) => setFormData({ ...formData, is_active: value ? 1 : 0 })}
                label={formData.is_active === 1 ? 'Active User' : 'Inactive User'}
              />
            </div>

            <div className="flex justify-end gap-3">
              <CancelButton
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUserForEdit(null);
                  resetForm();
                }}
              />
              <UpdateButton
                onClick={handleUpdateUser}
              >
                Update User
              </UpdateButton>
            </div>
        </div>
      </EnhancedModal>
    </div>
  );
};

export default Users;